import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall, ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { FaMicrophone, FaPlay, FaClock, FaUser, FaFilter, FaPlus, FaFileAudio, FaEdit, FaPause, FaRedo, FaTrash, FaDownload } from 'react-icons/fa';
import AudioRecorder from '../../components/AudioRecorder';
import PatientSearchDropdown from '../../components/PatientSearchDropdown';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

interface Dictation {
  id: number;
  user?: number; // user ID who created the dictation
  patient?: number; // patient ID (UUID)
  patient_name: string;
  patient_dob: string;
  patient_gender?: string;
  patient_client_id?: string;
  dictation_type?: string;
  dictation_number?: number;
  dictation_reference?: string;
  user_name?: string; // Name of the user who created the dictation
  clinician_name?: string;
  created_at: string;
  duration: string; // ISO duration format from backend
  duration_seconds: number; // Converted to seconds for display
  status: 'recorded' | 'pending' | 'processing' | 'transcribed' | 'completed';
  file_path?: string;
  audio_file?: string;
}

const Dictations: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [dictations, setDictations] = useState<Dictation[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [providers, setProviders] = useState<{id: number, name: string}[]>([]);
  const [dictationTypeFilter, setDictationTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showRecorder, setShowRecorder] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDictation, setSelectedDictation] = useState<Dictation | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string>('');
  const [selectedPatientName, setSelectedPatientName] = useState<string>('');
  const [selectedDictationType, setSelectedDictationType] = useState<string>('');
  const [availableDictationTypes, setAvailableDictationTypes] = useState<string[]>([]);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    loadDictations();
    loadDictationTypes();
  }, [statusFilter, token]);

  const loadDictationTypes = async () => {
    if (!token) return;
    try {
      const response = await apiCall('/letters/dictation-types/?active_only=true', {
        method: 'GET'
      }, token);
      
      if (response.ok) {
        const data = await response.json();
        const types = (data.results || data).map((t: any) => t.name);
        setAvailableDictationTypes(types);
      }
    } catch (error) {
      console.error('[Dictations] Error loading dictation types:', error);
    }
  };

  const loadDictations = async () => {
    if (!token) {
      console.warn('[Dictations] No token available, skipping load');
      return;
    }
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      console.log('[Dictations] Loading dictations with filter:', statusFilter);
      const response = await apiCall(`/letters/dictations/${params}`, {
        method: 'GET'
      }, token);
      
      console.log('[Dictations] Response status:', response.status);
      console.log('[Dictations] Response OK:', response.ok);
      
      if (response.ok) {
        const data = await response.json();
        console.log('[Dictations] Loaded data:', data);
        console.log('[Dictations] Results array:', data.results || data);
        console.log('[Dictations] Results count:', (data.results || data).length);
        const dictationsData = data.results || data;
        setDictations(dictationsData);
        
        // Extract unique providers
        const uniqueProviders = Array.from(
          new Map(
            dictationsData
              .filter((d: Dictation) => d.user && d.user_name)
              .map((d: Dictation) => [d.user, { id: d.user!, name: d.user_name! }])
          ).values()
        ) as {id: number, name: string}[];
        setProviders(uniqueProviders);
      } else {
        const errorText = await response.text();
        console.error('[Dictations] Failed to load dictations:', response.status, errorText);
      }
    } catch (error) {
      console.error('[Dictations] Error loading dictations:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      recorded: 'badge-info',
      pending: 'badge-warning',
      processing: 'badge-info',
      transcribed: 'badge-success',
      completed: 'badge-neutral'
    };
    return badges[status] || 'badge-ghost';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      recorded: 'Recorded',
      pending: 'Awaiting Transcription',
      processing: 'Processing',
      transcribed: 'Transcribed',
      completed: 'Completed'
    };
    return labels[status] || status;
  };

  const handleViewTranscript = (dictation: Dictation) => {
    // Navigate to GenerateLetter with dictation data
    navigate(`/letters/generate?dictationId=${dictation.id}`);
  };

  const handleSaveRecording = async (audioBlob: Blob, duration: number, patientId?: string, dictationType?: string) => {
    try {
      const formData = new FormData();
      formData.append('audio_file', audioBlob, 'recording.webm');
      formData.append('duration', duration.toString());
      if (patientId) {
        formData.append('patient_id', patientId);
      }
      if (dictationType) {
        formData.append('dictation_type', dictationType);
      }

      const response = await apiCall(ENDPOINTS.DICTATIONS.CREATE, {
        method: 'POST',
        body: formData,
      }, token || undefined);

      if (response.ok) {
        const data = await response.json();
        // Close the recorder modal
        setShowRecorder(false);
        // Refresh the dictations list
        await loadDictations();
        // Show success message
        const patientName = data.patient_name || 'Unknown Patient';
        const dictationType = data.dictation_type || 'dictation';
        showToast(`Dictation for ${patientName} saved, "${dictationType}"`, 'success');
      } else {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        throw new Error('Failed to save dictation');
      }
    } catch (error) {
      console.error('Error saving dictation:', error);
      alert('Failed to save dictation. Please try again.');
    }
  };

  const handleGenerateLetter = async (dictation: Dictation) => {
    // If stuck in processing, reset first
    if (dictation.status === 'processing') {
      if (!confirm('This dictation is stuck in processing. Reset and retry?')) {
        return;
      }
      
      try {
        const response = await apiCall(
          ENDPOINTS.DICTATIONS.RESET_STATUS(dictation.id),
          { method: 'POST' },
          token || undefined
        );
        
        if (response.ok) {
          showToast('Status reset. Opening letter generator...', 'success');
          // Reload dictations to update UI
          await loadDictations();
          // Small delay to ensure state is updated
          setTimeout(() => {
            navigate(`/letters/generate?dictationId=${dictation.id}`);
          }, 500);
        } else {
          const error = await response.json();
          showToast(error.error || 'Failed to reset status', 'error');
        }
      } catch (error) {
        console.error('[Dictations] Error resetting status:', error);
        showToast('Error resetting dictation status', 'error');
      }
      return;
    }
    
    // Navigate to generate letter page with dictation data
    navigate(`/letters/generate?dictationId=${dictation.id}`);
  };

  const handleEditPatient = (dictation: Dictation) => {
    setSelectedDictation(dictation);
    setSelectedPatientId(dictation.patient?.toString() || '');
    setSelectedPatientName(dictation.patient_name || '');
    setSelectedDictationType(dictation.dictation_type || '');
    setShowEditModal(true);
  };

  const handleUpdatePatient = async () => {
    if (!selectedDictation || !selectedPatientId) {
      showToast('Please select a patient', 'error');
      return;
    }

    try {
      console.log('[Dictations] Updating patient for dictation:', selectedDictation.id);
      console.log('[Dictations] New patient ID:', selectedPatientId);
      console.log('[Dictations] Endpoint:', ENDPOINTS.DICTATIONS.UPDATE_PATIENT(selectedDictation.id));
      
      const requestBody: any = { patient_id: selectedPatientId };
      if (selectedDictationType && selectedDictationType !== selectedDictation.dictation_type) {
        requestBody.dictation_type = selectedDictationType;
      }
      console.log('[Dictations] Request body:', requestBody);
      
      const response = await apiCall(
        ENDPOINTS.DICTATIONS.UPDATE_PATIENT(selectedDictation.id),
        {
          method: 'PATCH',
          body: JSON.stringify(requestBody),
        },
        token || undefined
      );

      console.log('[Dictations] Update response status:', response.status);
      console.log('[Dictations] Update response OK:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('[Dictations] Update successful:', data);
        showToast('Patient updated successfully', 'success');
        setShowEditModal(false);
        loadDictations(); // Refresh list
      } else {
        const errorText = await response.text();
        console.error('[Dictations] Update failed:', errorText);
        let errorMessage = 'Failed to update patient';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.detail || errorMessage;
        } catch (e) {
          console.error('[Dictations] Error parsing error response:', e);
        }
        showToast(errorMessage, 'error');
      }
    } catch (error) {
      console.error('[Dictations] Error updating patient:', error);
      showToast('Error updating patient', 'error');
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  };

  const handleSortByReference = () => {
    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortOrder(newOrder);
    const sorted = [...dictations].sort((a, b) => {
      const numA = a.dictation_number || 0;
      const numB = b.dictation_number || 0;
      return newOrder === 'asc' ? numA - numB : numB - numA;
    });
    setDictations(sorted);
  };

  const handleProviderToggle = (providerId: number) => {
    setSelectedProviders(prev => 
      prev.includes(providerId)
        ? prev.filter(id => id !== providerId)
        : [...prev, providerId]
    );
  };

  const handleSelectAllProviders = () => {
    if (selectedProviders.length === providers.length) {
      setSelectedProviders([]);
    } else {
      setSelectedProviders(providers.map(p => p.id));
    }
  };

  // Apply all filters
  const filteredDictations = dictations.filter(dictation => {
    // Status filter
    if (statusFilter !== 'all' && dictation.status !== statusFilter) {
      return false;
    }
    
    // Provider filter
    if (selectedProviders.length > 0 && dictation.user && !selectedProviders.includes(dictation.user)) {
      return false;
    }
    
    // Dictation type filter
    if (dictationTypeFilter !== 'all' && dictation.dictation_type !== dictationTypeFilter) {
      return false;
    }
    
    // Search filter (by dictation reference or patient name)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesReference = dictation.dictation_reference?.toLowerCase().includes(query);
      const matchesPatient = dictation.patient_name?.toLowerCase().includes(query);
      if (!matchesReference && !matchesPatient) {
        return false;
      }
    }
    
    return true;
  });

  // Get unique dictation types from API list and existing dictations
  const dictationTypes = Array.from(
    new Set([
      ...availableDictationTypes,
      ...dictations.map(d => d.dictation_type).filter((type): type is string => Boolean(type))
    ])
  );

  const handlePlayAudio = (dictation: Dictation) => {
    if (!dictation.audio_file) {
      showToast('No audio file available', 'error');
      return;
    }

    // If already playing this audio, pause it
    if (currentlyPlaying === dictation.id && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setCurrentlyPlaying(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    // Create new audio element and play
    const audio = new Audio(dictation.audio_file);
    audio.onended = () => setCurrentlyPlaying(null);
    audio.onerror = () => {
      showToast('Failed to play audio', 'error');
      setCurrentlyPlaying(null);
    };
    
    audio.play().then(() => {
      audioRef.current = audio;
      setCurrentlyPlaying(dictation.id);
    }).catch(() => {
      showToast('Failed to play audio', 'error');
    });
  };

  const handlePauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setCurrentlyPlaying(null);
    }
  };

  const handleRestartAudio = (dictation: Dictation) => {
    if (audioRef.current && currentlyPlaying === dictation.id) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      handlePlayAudio(dictation);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const handleDeleteDictation = async (dictation: Dictation) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete dictation ${dictation.dictation_reference}?\n\nThis action cannot be undone.`
    );

    if (!confirmDelete) return;

    try {
      const response = await apiCall(
        `/letters/dictations/${dictation.id}/`,
        { method: 'DELETE' },
        token || undefined
      );

      if (response.ok) {
        showToast('Dictation deleted successfully', 'success');
        loadDictations(); // Refresh list
      } else {
        const errorText = await response.text();
        console.error('[Dictations] Delete failed:', errorText);
        showToast('Failed to delete dictation', 'error');
      }
    } catch (error) {
      console.error('[Dictations] Error deleting dictation:', error);
      showToast('Error deleting dictation', 'error');
    }
  };

  const handleDownloadAudio = async (dictation: Dictation) => {
    if (!dictation.audio_file) {
      showToast('No audio file available', 'error');
      return;
    }

    try {
      // Fetch the file as a blob to force download
      const response = await fetch(dictation.audio_file);
      if (!response.ok) {
        throw new Error('Failed to fetch audio file');
      }
      
      const blob = await response.blob();
      
      // Create object URL from blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create temporary anchor element
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${dictation.dictation_reference || 'dictation'}.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up object URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      
      showToast('Audio file downloaded', 'success');
    } catch (error) {
      console.error('[Dictations] Error downloading audio:', error);
      showToast('Failed to download audio file', 'error');
    }
  };

  return (
    <div className="p-6">
      <Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
            <FaMicrophone className="text-primary" />
            Dictations
          </h1>
        </div>
        <button
          onClick={() => setShowRecorder(true)}
          className="btn btn-primary gap-2"
        >
          <FaPlus />
          New Dictation
        </button>
      </div>

      {/* Filter Section */}
      <div className="bg-base-200 rounded-lg p-4 mb-6">
        <div className="flex items-center gap-6">
          {/* Status Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium whitespace-nowrap">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select select-bordered w-48"
            >
              <option value="all">All Statuses</option>
              <option value="recorded">Recorded</option>
              <option value="pending">Awaiting Transcription</option>
              <option value="processing">Processing</option>
              <option value="transcribed">Transcribed</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Dictation Type Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium whitespace-nowrap">Type:</span>
            <select
              value={dictationTypeFilter}
              onChange={(e) => setDictationTypeFilter(e.target.value)}
              className="select select-bordered w-48"
            >
              <option value="all">All Types</option>
              {dictationTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Search Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium whitespace-nowrap">Search:</span>
            <input
              type="text"
              placeholder="Ref # or Patient name..."
              className="input input-bordered w-64"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Provider Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium whitespace-nowrap">Providers:</span>
            <div className="dropdown dropdown-end">
              <button tabIndex={0} className="btn btn-outline w-48 justify-between">
                <span>
                  {selectedProviders.length === 0 
                    ? 'All Providers' 
                    : selectedProviders.length === providers.length
                    ? 'All Providers'
                    : `${selectedProviders.length} selected`}
                </span>
                <span>▼</span>
              </button>
              <ul tabIndex={0} className="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-64 max-h-96 overflow-y-auto z-10">
                <li>
                  <label className="label cursor-pointer justify-start gap-2">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-sm"
                      checked={selectedProviders.length === providers.length}
                      onChange={handleSelectAllProviders}
                    />
                    <span className="font-semibold">All Providers</span>
                  </label>
                </li>
                <li className="menu-title"><span>Select Providers</span></li>
                {providers.map(provider => (
                  <li key={provider.id}>
                    <label className="label cursor-pointer justify-start gap-2">
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm checkbox-primary"
                        checked={selectedProviders.includes(provider.id)}
                        onChange={() => handleProviderToggle(provider.id)}
                      />
                      <span>{provider.name}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Dictations List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-base-content opacity-70">Loading dictations...</p>
        </div>
      ) : filteredDictations.length > 0 ? (
        <div>
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>
                  <button 
                    onClick={handleSortByReference}
                    className="flex items-center gap-1 hover:text-primary cursor-pointer"
                  >
                    Ref #
                    <span className="text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>
                  </button>
                </th>
                <th>Patient</th>
                <th>DOB</th>
                <th>Gender</th>
                <th>Dictation Type</th>
                <th>Created</th>
                <th>Duration</th>
                <th>Dictated By</th>
                <th>Status</th>
                <th>Playback</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDictations.map((dictation) => (
                <tr key={dictation.id} className="hover">
                  <td className="font-mono">
                    {dictation.dictation_reference || 'N/A'}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <FaUser className="text-base-content opacity-50" />
                      <span className="font-medium">{dictation.patient_name || 'Unknown'}</span>
                    </div>
                  </td>
                  <td>
                    {dictation.patient_dob 
                      ? formatDate(dictation.patient_dob)
                      : '-'
                    }
                  </td>
                  <td className="text-center">
                    {dictation.patient_gender || '-'}
                  </td>
                  <td>
                    <span className="badge badge-outline">
                      {dictation.dictation_type || 'Not specified'}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <FaClock className="text-base-content opacity-50" />
                      {formatDateTime(dictation.created_at)}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <FaMicrophone className="text-primary" />
                      {formatDuration(dictation.duration_seconds)}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <FaUser className="text-primary opacity-70" />
                      <span className="font-medium">{dictation.user_name || 'Unknown User'}</span>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(dictation.status)}`}>
                      {getStatusLabel(dictation.status)}
                    </span>
                  </td>
                  <td>
                    {dictation.audio_file ? (
                      <div className="flex gap-1">
                        {currentlyPlaying === dictation.id ? (
                          <button
                            onClick={handlePauseAudio}
                            className="btn btn-ghost btn-xs"
                            title="Pause"
                          >
                            <FaPause className="text-primary" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handlePlayAudio(dictation)}
                            className="btn btn-ghost btn-xs"
                            title="Play"
                          >
                            <FaPlay className="text-primary" />
                          </button>
                        )}
                        <button
                          onClick={() => handleRestartAudio(dictation)}
                          className="btn btn-ghost btn-xs"
                          title="Restart"
                        >
                          <FaRedo className="text-base-content opacity-50" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-base-content opacity-50 text-xs">No audio</span>
                    )}
                  </td>
                  <td>
                    <div className="flex gap-2">
                      {dictation.status === 'recorded' && (
                        <button
                          onClick={() => handleGenerateLetter(dictation)}
                          className="btn btn-sm btn-primary gap-2"
                        >
                          <FaFileAudio />
                          Generate Letter
                        </button>
                      )}
                      {dictation.status === 'processing' && (
                        <button
                          onClick={() => handleGenerateLetter(dictation)}
                          className="btn btn-sm btn-warning gap-2"
                          title="Reset and retry transcription"
                        >
                          <FaRedo />
                          Retry
                        </button>
                      )}
                      {dictation.status === 'pending' && (
                        <button
                          onClick={() => handleViewTranscript(dictation)}
                          className="btn btn-sm btn-primary gap-2"
                        >
                          <FaPlay />
                          Transcribe
                        </button>
                      )}
                      {(dictation.status === 'transcribed' || dictation.status === 'completed') && (
                        <button
                          onClick={() => handleViewTranscript(dictation)}
                          className="btn btn-sm btn-outline gap-2"
                        >
                          View
                        </button>
                      )}
                      <button
                        onClick={() => handleEditPatient(dictation)}
                        className="btn btn-ghost btn-sm gap-2"
                        title="Edit Patient"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDownloadAudio(dictation)}
                        className="btn btn-ghost btn-sm"
                        title="Download"
                      >
                        <FaDownload className="text-primary" />
                      </button>
                      <button
                        onClick={() => handleDeleteDictation(dictation)}
                        className="btn btn-ghost btn-sm"
                        title="Delete"
                      >
                        <FaTrash className="text-error" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 text-base-content">
          <FaMicrophone className="text-6xl mx-auto mb-4 text-base-content opacity-30" />
          <p className="text-lg mb-2 opacity-60">No dictations found</p>
          <p className="text-sm opacity-60">Start by creating a new dictation</p>
          <button
            onClick={() => setShowRecorder(true)}
            className="btn btn-primary btn-sm mt-4 gap-2"
          >
            <FaPlus />
            New Dictation
          </button>
        </div>
      )}

      {/* Audio Recorder Modal */}
      {showRecorder && (
        <AudioRecorder
          isOpen={showRecorder}
          onClose={() => setShowRecorder(false)}
          onSave={handleSaveRecording}
        />
      )}

      {/* Edit Patient Modal */}
      {showEditModal && selectedDictation && (
        <div className="modal modal-open">
          <div className="modal-box overflow-visible">
            <h3 className="font-bold text-lg mb-4">Edit Patient for Dictation</h3>
            
            <div className="mb-4">
              <label className="label">
                <span className="label-text">Dictation Reference</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full"
                value={selectedDictation.dictation_reference || 'N/A'}
                disabled
              />
            </div>

            <div className="mb-4 relative z-[100]">
              <label className="label">
                <span className="label-text">Select Patient</span>
              </label>
              <PatientSearchDropdown
                value={selectedPatientName}
                onChange={(patientId, patientName) => {
                  setSelectedPatientId(patientId);
                  setSelectedPatientName(patientName);
                }}
                placeholder="Search by name or client ID..."
              />
            </div>

            <div className="mb-4">
              <label className="label">
                <span className="label-text">Dictation Type</span>
              </label>
              <select
                className="select select-bordered w-full"
                value={selectedDictationType}
                onChange={(e) => setSelectedDictationType(e.target.value)}
              >
                <option value="">Select dictation type...</option>
                {availableDictationTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-action">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedPatientId('');
                  setSelectedPatientName('');
                  setSelectedDictationType('');
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdatePatient}
                disabled={!selectedPatientId}
              >
                Update Patient
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dictations;
